import { Component } from 'react';

import { GetEarlyRepaymentSimulationResponse } from '@iwoca/lapi-client/edge';
import { FormattedMessage } from 'react-intl';

import styles from './RepaymentAmountBreakdown.module.css';
import { FormattedCurrency } from '../../../../iwIntlProvider/FormattedCurrency';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals';

type TProps = {
    loadingNewRepaymentSchedule: boolean | null;
    showSchedule?: (
        earlyRepaymentSimulationSchedule: GetEarlyRepaymentSimulationResponse
    ) => void;
    earlyRepaymentSimulationSchedule?: GetEarlyRepaymentSimulationResponse;
    remainingOutstandingBalance: number | null;
    numberOfNextRepayments: number | null;
    reducedNextRepayment: number | null;
    reducedNextRepaymentAmount: number | null;
    nextRepaymentStatus:
        'NORMAL' | 'PARTIAL_FUTURE' | 'PARTIAL_TODAY' | 'EXACT_AMOUNT' | null;
    followingRepaymentStatus: 'EQUAL' | 'FULL_REPAYMENT' | null;
    hasFollowingRepaymentsLeft?: boolean;
    followingRepaymentAmount?: number | null;
    error: boolean | null;
};

export class RepaymentAmountBreakdown extends Component<TProps> {
    handleShowSchedule = () => {
        if (!this.props.loadingNewRepaymentSchedule) {
            this.props.showSchedule!(
                this.props.earlyRepaymentSimulationSchedule!
            );
        }
    };

    getClassName() {
        if (this.props.loadingNewRepaymentSchedule) {
            return styles.RepaymentAmountBreakdownLoading;
        }

        return styles.RepaymentAmountBreakdown;
    }

    renderOutstandingBalance() {
        const { remainingOutstandingBalance } = this.props;

        if (remainingOutstandingBalance! >= 0) {
            return (
                <div data-testid="repayment-outstanding-balance">
                    <h3 className={styles.title}>
                        <FormattedMessage
                            id="_.early_repayment.choose_repayment_amount.remaining_outstanding"
                            defaultMessage="Reducing your balance to"
                        />
                    </h3>
                    <div className={styles.amount}>
                        <MoneyWithDecimals
                            className={styles.moneyLarge}
                            value={remainingOutstandingBalance!}
                            decimalWithSeparatorClassName={
                                styles.decimalsWithSeparator
                            }
                        />
                    </div>
                </div>
            );
        }
    }

    renderShowSchedule() {
        return (
            <div>
                <span
                    className={styles.showSchedule}
                    onClick={this.handleShowSchedule}
                >
                    <FormattedMessage
                        id="_.early_repayment.choose_repayment_amount.show_schedule"
                        defaultMessage="See your new schedule in full"
                    />
                </span>
            </div>
        );
    }

    renderNextRepayments() {
        const { numberOfNextRepayments, reducedNextRepayment } = this.props;

        const reducedRepaymentText = (
            <FormattedMessage
                id="_.early_repayment.choose_repayment_amount.next_repayment.reduced_repayment_text"
                defaultMessage="{numberOfNextRepayments} repayments"
                values={{ numberOfNextRepayments }}
            />
        );
        const reducedNextRepaymentAmount = (
            <FormattedCurrency value={this.props.reducedNextRepaymentAmount!} />
        );

        const reducedNextRepaymentBreakdownText = () => {
            switch (this.props.nextRepaymentStatus) {
                case 'NORMAL':
                    return (
                        <p className={styles.breakdownText}>
                            <FormattedMessage
                                id="_.early_repayment.choose_repayment_amount.next_repayment.normal"
                                defaultMessage="This will cover your next {reducedRepaymentText} and reduce your {nextReducedRepaymentText}."
                                values={{
                                    reducedRepaymentText: (
                                        <b>{reducedRepaymentText}</b>
                                    ),
                                    nextReducedRepaymentText: (
                                        <b>
                                            {/* eslint-disable-next-line formatjs/blocklist-elements */}
                                            <FormattedMessage
                                                id="_.early_repayment.choose_repayment_amount.next_repayment.nextReduced_repayment_text"
                                                defaultMessage="{reducedNextRepayment, selectordinal, one {#st} two {#nd} few {#rd} other {#th}} repayment to {reducedNextRepaymentAmount}"
                                                values={{
                                                    reducedNextRepayment,
                                                    reducedNextRepaymentAmount
                                                }}
                                            />
                                        </b>
                                    )
                                }}
                            />
                        </p>
                    );
                case 'PARTIAL_FUTURE':
                    return (
                        <p className={styles.breakdownText}>
                            <FormattedMessage
                                id="_.early_repayment.choose_repayment_amount.next_repayment.partial_next_repayment"
                                defaultMessage="This will reduce your next payment to {reducedNextRepaymentAmount}"
                                values={{ reducedNextRepaymentAmount }}
                            />
                        </p>
                    );
                case 'PARTIAL_TODAY':
                    return (
                        <p className={styles.breakdownText}>
                            <FormattedMessage
                                id="_.early_repayment.choose_repayment_amount.next_repayment.partial_today_next_repayment"
                                defaultMessage="This will reduce the amount due today to {reducedNextRepaymentAmount}"
                                values={{ reducedNextRepaymentAmount }}
                            />
                        </p>
                    );
                case 'EXACT_AMOUNT':
                    return (
                        <p className={styles.breakdownText}>
                            <FormattedMessage
                                id="_.early_repayment.choose_repayment_amount.next_repayment.exact_amount"
                                defaultMessage="This will cover your next {reducedRepaymentText}."
                                values={{ reducedRepaymentText }}
                            />
                        </p>
                    );
            }
        };

        const breakdownText = reducedNextRepaymentBreakdownText();

        return <div className={styles.breakdown}>{breakdownText}</div>;
    }

    renderFollowingRepayments() {
        const { followingRepaymentStatus, hasFollowingRepaymentsLeft } =
            this.props;

        if (!hasFollowingRepaymentsLeft) {
            return (
                <FormattedMessage
                    id="_.early_repayment.choose_repayment_amount.no_further_repayments"
                    defaultMessage="You have no further repayments after that"
                />
            );
        }

        switch (followingRepaymentStatus) {
            case 'EQUAL':
                return (
                    <div>
                        <p className={styles.breakdownText}>
                            <FormattedMessage
                                id="_.early_repayment.choose_repayment_amount.following_repayments.equal"
                                defaultMessage="Your following repayments will stay the same at {followingRepaymentAmount} per month."
                                values={{
                                    followingRepaymentAmount: (
                                        <FormattedCurrency
                                            value={
                                                this.props
                                                    .followingRepaymentAmount!
                                            }
                                        />
                                    )
                                }}
                            />
                        </p>
                    </div>
                );

            case 'FULL_REPAYMENT':
                return (
                    <div>
                        <FormattedMessage
                            id="_.early_repayment.choose_repayment_amount.repayments_covered"
                            defaultMessage="This will cover all of your repayments"
                        />
                    </div>
                );
        }
    }

    renderDetails() {
        if (this.props.error) {
            const showSchedule = this.renderShowSchedule();
            return (
                <>
                    <div>
                        <FormattedMessage
                            id="_.early_repayment.choose_repayment_amount.unsupported"
                            defaultMessage="Your repayment schedule will be adjusted to take into account your early repayment."
                        />
                    </div>
                    {showSchedule}
                </>
            );
        } else if (this.props.remainingOutstandingBalance! > 0) {
            const nextRepayment = this.renderNextRepayments();
            const followingRepayment = this.renderFollowingRepayments();
            const showSchedule = this.renderShowSchedule();

            return (
                <div
                    className={styles.details}
                    data-testid="repayment-breakdown"
                >
                    {nextRepayment}
                    {followingRepayment}
                    {showSchedule}
                </div>
            );
        } else if (this.props.remainingOutstandingBalance === 0) {
            const followingRepayment = this.renderFollowingRepayments();
            return <div className={styles.details}>{followingRepayment}</div>;
        }
    }

    render() {
        const outstandingBalance = this.renderOutstandingBalance();
        const details = this.renderDetails();

        return (
            <div className={this.getClassName()}>
                {outstandingBalance}
                {details}
            </div>
        );
    }
}
